import { useAppDispatch } from '../../store';
import { useUser, saveUserThunk } from '../../store/User';
import { useLaunchDarkly } from '../../store/LaunchDarkly';
import { addFeatureOptimistic, removeFeatureOptimistic, setFeaturesOptimistic } from '../../store/User/User';
import { hasUserVisitedFeature, hasUserSeenAlert } from '../features/featureMapping';

export const useFeatures = () => {
  const dispatch = useAppDispatch();
  const { data: user } = useUser();
  const launchDarkly = useLaunchDarkly();

  const features = user?.features || [];
  const isUserDataLoaded = !!user;
  const isNewBadgeFeatureEnabled = launchDarkly?.['new-badge-feature-enabled'] ?? false;

  const hasVisitedFeature = (featureName: string): boolean => {
    if (!isNewBadgeFeatureEnabled) return true;
    return hasUserVisitedFeature(featureName, features);
  };

  const hasSeenAlert = (alertName: string): boolean => {
    if (!isNewBadgeFeatureEnabled) return true;
    return hasUserSeenAlert(alertName, features);
  };

  const addFeature = (featureName: string) => {
    if (!user || !isNewBadgeFeatureEnabled) return;

    dispatch(addFeatureOptimistic(featureName));

    const updatedFeatures = [...(user.features || []), featureName];
    void dispatch(
      saveUserThunk({
        features: updatedFeatures,
      }),
    );
  };

  const removeFeature = (featureName: string) => {
    if (!user || !isNewBadgeFeatureEnabled) return;

    dispatch(removeFeatureOptimistic(featureName));

    const updatedFeatures = (user.features || []).filter(f => f !== featureName);
    void dispatch(
      saveUserThunk({
        features: updatedFeatures,
      }),
    );
  };

  const setFeatures = (newFeatures: string[]) => {
    if (!user) return;

    dispatch(setFeaturesOptimistic(newFeatures));

    void dispatch(
      saveUserThunk({
        features: newFeatures,
      }),
    );
  };

  return {
    // Read operations
    features,
    hasVisitedFeature,
    hasSeenAlert,
    isUserDataLoaded,
    // Write operations
    addFeature,
    removeFeature,
    setFeatures,
  };
};
